import React, { ReactNode, useState } from 'react';
import { t, c, jt } from 'ttag';
import { Form, message, Switch } from 'antd';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import { Renderer } from '@eventmobi/rich-text';
// @ts-ignore
import Editor, { TOOLBAR_BUTTONS } from '@eventmobi/rich-text/Editor';
// @ts-ignore
import { isValid as isValidHTML } from '@eventmobi/rich-text/html';
// @ts-ignore
import { hasValidFields } from '@eventmobi/rich-text/dynamicFields';
// @ts-ignore
import RichTextLinkStyles from '@eventmobi/rich-text/OverrideLinkStyles';

import { Icons } from '../IconPicker/Icons';
import useTranslation from '../../hooks/useTranslation';
import { getEventUrls } from '../../state/event';
import Emphasis from '../Emphasis';
import Fonts, { useFonts } from '../Fonts';

const ALLOWED_TOOLBAR_BUTTONS = [
  TOOLBAR_BUTTONS.BOLD,
  TOOLBAR_BUTTONS.ITALIC,
  TOOLBAR_BUTTONS.UNDERLINE,
  TOOLBAR_BUTTONS.COLOR,
  TOOLBAR_BUTTONS.ALIGN,
  TOOLBAR_BUTTONS.UL,
  TOOLBAR_BUTTONS.OL,
  TOOLBAR_BUTTONS.LINK,
  TOOLBAR_BUTTONS.UNLINK,
  TOOLBAR_BUTTONS.IMAGE,
  TOOLBAR_BUTTONS.ERASER,
  TOOLBAR_BUTTONS.SOURCE,
  TOOLBAR_BUTTONS.FONTSIZE,
  TOOLBAR_BUTTONS.FONT,
  TOOLBAR_BUTTONS.CUSTOM_FONTS,
];

const SUPPORT_PAGE_LINK_HTML_TAGS =
  'https://help.eventmobi.com/en/knowledge/what-html-tags-does-the-rich-text-editor-support#';

interface WrapperProps {
  $linkColor: string;
}

const Wrapper = styled.div<WrapperProps>`
  width: 100%;
  padding: 0;
  display: block;
  & a {
    color: ${props => props.$linkColor};
  }
`;

const Toggle = styled.div`
  margin-bottom: 9px;
  display: flex;
  justify-content: space-between;
`;

const knowledgeBaseLink = (
  <a href={SUPPORT_PAGE_LINK_HTML_TAGS} target="_blank" rel="noreferrer noopener">
    {c('As in: Click here to go the knowledge base support page.').t`here`}
  </a>
);

const onPasteError = () => {
  message.warning(
    jt`Invalid HTML tags were removed from the pasted content. Click ${knowledgeBaseLink} for supported HTML tags.`,
  );
};

export interface DynamicFieldsOptsTypes {
  extraLocale?: string;
  fields?: {
    id: string;
    label: string;
    isExtra?: boolean;
  }[];
  controlsComponent?: () => void;
}

// TODO: Once we have types in toolkit, this should extend toolkit's exported props, since
// we're spreading the rest parameters
export interface EditorWrapperProps {
  value?: string;
  onChange?: () => void;
  eventId: string | number;
  showColor?: boolean;
  readOnly?: boolean;
  onPasteError?: () => void;
  id?: string;
  allowDarkMode?: boolean;

  // TODO: We need to update this after toolkit has types defined
  buttons?: typeof ALLOWED_TOOLBAR_BUTTONS;
  dynamicFieldsOpts?: DynamicFieldsOptsTypes;
  colorPickerLocale: {
    button: string;
    title: string;
  };
  linkColor: string;

  // TODO: Remove these after we're extending the toolkit types
  label?: ReactNode;
}

const EditorWrapper = ({
  eventId,
  onChange = () => {},
  linkColor,
  allowDarkMode = true,
  ...rest
}: EditorWrapperProps) => {
  const { language } = useTranslation();
  const eventUrls = useSelector(getEventUrls);
  const {
    visible: isLibraryVisible,
    fonts,
    updateFonts,
    setVisible: setLibraryVisible,
  } = useFonts();

  const [isDarkModeEnabled, setDarkModeEnabled] = useState(false);

  return (
    <>
      {eventUrls && (
        <RichTextLinkStyles icon={Icons.externalLink} color={linkColor} urls={eventUrls} />
      )}
      {allowDarkMode && (
        <Toggle>
          {t`Working with white or light colored text? Use Dark mode.`}
          <Switch checked={isDarkModeEnabled} onChange={() => setDarkModeEnabled(prev => !prev)} />
        </Toggle>
      )}
      <Wrapper $linkColor={linkColor} className="ant-input">
        <Editor
          {...rest}
          language={language}
          onPasteError={onPasteError}
          onChange={onChange}
          moreFontsLabel={t`More Fonts`}
          customFonts={fonts}
          onAddFonts={() => setLibraryVisible(true)}
          isDarkMode={isDarkModeEnabled}
        />
        {isLibraryVisible && (
          <Fonts onSave={updateFonts} fonts={fonts} onClose={() => setLibraryVisible(false)} />
        )}
      </Wrapper>
    </>
  );
};

export const DEFAULT_TOOLBAR_BUTTONS_TYPE = 'DEFAULT';
export const WIDGET_DESIGNER_TOOLBAR_BUTTONS_TYPE = 'WIDGET_DESIGNER';
export const BADGE_DESIGNER_TOOLBAR_BUTTONS_TYPE = 'BADGE_DESIGNER';
export const BADGE_DESIGNER_SIMPLE_LABEL_TOOLBAR_BUTTONS_TYPE = 'BADGE_DESIGNER_LABEL';
export const CONFIRMATION_EMAIL_TOOLBAR_BUTTONS_TYPE = 'CONFIRMATION_EMAIL';

const TOOLBAR_BUTTONS_TYPE = {
  [DEFAULT_TOOLBAR_BUTTONS_TYPE]: [
    TOOLBAR_BUTTONS.CUSTOM_FONTS,
    TOOLBAR_BUTTONS.FONTSIZE,
    TOOLBAR_BUTTONS.BOLD,
    TOOLBAR_BUTTONS.ITALIC,
    TOOLBAR_BUTTONS.UNDERLINE,
    TOOLBAR_BUTTONS.COLOR,
    TOOLBAR_BUTTONS.ALIGN,
    TOOLBAR_BUTTONS.UL,
    TOOLBAR_BUTTONS.OL,
    TOOLBAR_BUTTONS.LINK,
    TOOLBAR_BUTTONS.UNLINK,
    TOOLBAR_BUTTONS.IMAGE,
    TOOLBAR_BUTTONS.ERASER,
    TOOLBAR_BUTTONS.SOURCE,
  ],
  [WIDGET_DESIGNER_TOOLBAR_BUTTONS_TYPE]: [
    TOOLBAR_BUTTONS.CUSTOM_FONTS,
    TOOLBAR_BUTTONS.FONTSIZE,
    TOOLBAR_BUTTONS.BOLD,
    TOOLBAR_BUTTONS.ITALIC,
    TOOLBAR_BUTTONS.UNDERLINE,
    TOOLBAR_BUTTONS.COLOR,
    TOOLBAR_BUTTONS.ALIGN,
    TOOLBAR_BUTTONS.UL,
    TOOLBAR_BUTTONS.OL,
    TOOLBAR_BUTTONS.LINK,
    TOOLBAR_BUTTONS.UNLINK,
    TOOLBAR_BUTTONS.ERASER,
    TOOLBAR_BUTTONS.SOURCE,
  ],
  [BADGE_DESIGNER_TOOLBAR_BUTTONS_TYPE]: [
    TOOLBAR_BUTTONS.CUSTOM_FONTS,
    TOOLBAR_BUTTONS.FONTSIZE,
    TOOLBAR_BUTTONS.BOLD,
    TOOLBAR_BUTTONS.ITALIC,
    TOOLBAR_BUTTONS.UNDERLINE,
    TOOLBAR_BUTTONS.COLOR,
    TOOLBAR_BUTTONS.ALIGN,
    TOOLBAR_BUTTONS.UL,
    TOOLBAR_BUTTONS.OL,
  ],
  [BADGE_DESIGNER_SIMPLE_LABEL_TOOLBAR_BUTTONS_TYPE]: [
    TOOLBAR_BUTTONS.CUSTOM_FONTS,
    TOOLBAR_BUTTONS.FONTSIZE,
    TOOLBAR_BUTTONS.BOLD,
    TOOLBAR_BUTTONS.ITALIC,
    TOOLBAR_BUTTONS.UNDERLINE,
    TOOLBAR_BUTTONS.ALIGN,
    TOOLBAR_BUTTONS.UL,
    TOOLBAR_BUTTONS.OL,
  ],
  [CONFIRMATION_EMAIL_TOOLBAR_BUTTONS_TYPE]: [
    TOOLBAR_BUTTONS.BOLD,
    TOOLBAR_BUTTONS.ITALIC,
    TOOLBAR_BUTTONS.UNDERLINE,
    TOOLBAR_BUTTONS.COLOR,
    TOOLBAR_BUTTONS.ALIGN,
    TOOLBAR_BUTTONS.UL,
    TOOLBAR_BUTTONS.OL,
    TOOLBAR_BUTTONS.LINK,
    TOOLBAR_BUTTONS.UNLINK,
  ],
};

export interface Props
  extends Omit<EditorWrapperProps, 'colorPickerLocale' | 'readOnly' | 'buttons'> {
  rules: {}[]; // TODO: Obviously this is not valid
  showColor?: boolean;
  disabled?: boolean;
  name: string | string[];
  buttonsType?:
    | typeof DEFAULT_TOOLBAR_BUTTONS_TYPE
    | typeof WIDGET_DESIGNER_TOOLBAR_BUTTONS_TYPE
    | typeof BADGE_DESIGNER_TOOLBAR_BUTTONS_TYPE
    | typeof CONFIRMATION_EMAIL_TOOLBAR_BUTTONS_TYPE;
  infoText?: string;
  eventId: number | string;
  dynamicFieldsOpts?: DynamicFieldsOptsTypes;
  onChange?: () => void;
  allowDarkMode?: boolean;
}

const RichTextEditor = ({
  rules,
  infoText = '',
  eventId,
  showColor = true,
  buttonsType = DEFAULT_TOOLBAR_BUTTONS_TYPE,
  disabled = false,
  name,
  dynamicFieldsOpts = undefined,
  linkColor,
  onChange = () => {},
  allowDarkMode = true,
  ...rest
}: Props) => {
  const checkValidation = async (_, value) => {
    if (dynamicFieldsOpts?.fields?.length > 0 && !hasValidFields(value, dynamicFieldsOpts.fields)) {
      throw new Error(
        t`This content contains one or more Custom Fields that no longer exist in your event. Please remove any invalid Custom Fields to continue.`,
      );
    }

    if (!isValidHTML(value)) {
      throw new Error(
        // @ts-ignore Ignoring this because the error is actually rendered as a React tag
        jt`The content contains invalid HTML tags. Please visit ${SUPPORT_PAGE_LINK_HTML_TAGS} for supported HTML tags.`,
      );
    }
  };

  return (
    <>
      <Form.Item
        {...rest}
        name={name}
        rules={[{ validator: checkValidation, validateTrigger: 'onSubmit' }, ...(rules || [])]}
        style={{ marginBottom: 0 }}
      >
        <EditorWrapper
          id={`${name}-RTE`}
          readOnly={disabled}
          eventId={eventId}
          showColor={showColor}
          colorPickerLocale={{
            button: t`Custom`,
            title: t`Select custom color`,
          }}
          buttons={TOOLBAR_BUTTONS_TYPE[buttonsType]}
          dynamicFieldsOpts={dynamicFieldsOpts}
          linkColor={linkColor}
          onChange={onChange}
          allowDarkMode={allowDarkMode}
        />
      </Form.Item>
      {infoText && <Emphasis>{infoText}</Emphasis>}
    </>
  );
};

export { Renderer as RichTextRenderer };
export default RichTextEditor;
